import DoospLayout from "@/components/doosp/DoospLayout";
import { Link } from "react-router-dom";
import { ShieldCheck, Award, QrCode, Download, ArrowRight, CheckCircle2, Globe2 } from "lucide-react";

export default function Certifications() {
  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">Certifications</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
            Independent competency <span className="text-red-800">credentials.</span>
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-3xl leading-relaxed">
            Every DOOSP certificate is a verifiable, downloadable PDF issued by IMCAC — an
            independent competency assessment and certification body. It documents that the holder has
            successfully completed a competency-based assessment. It is <strong>not</strong> a
            license to practice, an academic degree, or a substitute for registration by any
            national or professional authority.
          </p>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              What&rsquo;s on your certificate.
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                { title: "Your full name and profession", body: "Printed prominently so your employer can review the credential." },
                { title: "Unique certificate ID", body: "Every credential has an opaque ID that anyone can look up at /verify." },
                { title: "QR code for real-time verification", body: "Employers scan to confirm the credential was issued by IMCAC and is still current." },
                { title: "Signature of the DOOSP director", body: "Every certificate is signed by the Director of Occupational Safety Programs." },
                { title: "Two-year validity", body: "Clearly printed issue and expiry dates. Re-assessment available before expiry." },
              ].map((f) => (
                <li key={f.title} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">{f.title}</div>
                    <div className="text-sm text-zinc-600 mt-0.5">{f.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="rounded-2xl overflow-hidden border-2 border-zinc-200 shadow-2xl bg-white">
              <img src="/sample_certificate.jpg" alt="DOOSP sample certificate" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-mono uppercase tracking-widest text-red-800">Public verification</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3">
              Verify any certificate in seconds.
            </h2>
            <p className="text-zinc-600 mt-3">
              Employers, auditors, and hiring managers can independently verify a candidate&rsquo;s certification without contacting us.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: QrCode, title: "Scan the QR", body: "Every certificate PDF has a QR code that opens the public verification page." },
              { icon: Award, title: "Search by name", body: "No cert ID? Enter the candidate&rsquo;s name at /verify to see all their valid credentials." },
              { icon: Globe2, title: "Real-time status", body: "Verification always reflects the current status — active, expired, or revoked." },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-lg p-6 border border-zinc-200">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-red-50 mb-4">
                  <f.icon className="h-5 w-5 text-red-800" />
                </div>
                <div className="font-display font-bold text-lg">{f.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/verify" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white h-11 px-6 rounded-md text-sm font-medium">
              Verify a certificate <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-red-800 to-red-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Earn your first DOOSP certification.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/programs" className="bg-white text-red-800 hover:bg-red-50 h-11 px-6 inline-flex items-center gap-2 rounded-md text-sm font-medium">
              Browse programs <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </DoospLayout>
  );
}
